// Platform-specific landing page variants for PPC campaigns
// Usage: estimate.azaistudios.com?platform=wordpress-vip

export const PLATFORM_VARIANTS = {
  // ============================================
  // WORDPRESS VARIANTS
  // ============================================
  'wordpress-vip': {
    cmsKey: 'wordpress',
    suggestedCompanySize: 'enterprise',
    headline: 'WordPress VIP Alternative',
    subtitle: 'See how much you could save by migrating from WordPress VIP to Webflow.',
    metaTitle: 'WordPress VIP to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Eliminate $50K+ annual VIP licensing fees',
      'Visual editing without developer dependency',
      'Enterprise-grade security & 99.99% uptime',
      'No more plugin compatibility nightmares',
    ],
    faqs: [
      {
        question: 'How do you handle WordPress VIP custom functionality?',
        answer: 'We audit all custom plugins and VIP-specific code, then recreate functionality using Webflow\'s native features, custom code embeds, or third-party integrations. Most VIP customizations have direct Webflow equivalents or better alternatives.',
      },
      {
        question: 'Will we lose our SEO rankings during the migration?',
        answer: 'Absolutely not. We implement comprehensive 301 redirects, preserve all URL structures where possible, and transfer all meta data. Our migrations typically see improved Core Web Vitals scores, which can actually boost rankings.',
      },
      {
        question: 'What about our existing WordPress integrations?',
        answer: 'Webflow integrates with most enterprise tools via native integrations or Zapier. We map all your current integrations (CRM, marketing automation, analytics) and set up equivalent or improved workflows in Webflow.',
      },
      {
        question: 'How long does a WordPress VIP to Webflow migration take?',
        answer: 'Typically 8-14 weeks depending on site complexity. WordPress VIP sites often have extensive customizations, so we allocate extra time for functionality auditing and recreation.',
      },
    ],
    testimonials: [
      {
        quote: 'We were spending $75K/year on WordPress VIP plus another $50K on developer maintenance. Webflow cut our total cost by 60% and our marketing team can now make changes themselves.',
        author: 'VP of Marketing',
        company: 'Enterprise SaaS Company',
        metric: '60% cost reduction',
      },
      {
        quote: 'The migration was seamless. Azai handled everything from our complex ACF setups to our custom Gutenberg blocks. Our site is faster and easier to manage.',
        author: 'Director of Digital',
        company: 'B2B Technology Firm',
        metric: '3x faster page loads',
      },
    ],
  },

  'wordpress': {
    cmsKey: 'wordpressSelfHosted',
    suggestedCompanySize: 'midmarket',
    headline: 'WordPress to Webflow Migration',
    subtitle: 'Calculate your savings switching from WordPress to Webflow.',
    metaTitle: 'WordPress to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'End the plugin update treadmill forever',
      'No more security vulnerabilities or hacks',
      'Visual editing that actually works',
      'Hosting, SSL, and CDN included',
    ],
    faqs: [
      {
        question: 'Can you migrate my WordPress theme design exactly?',
        answer: 'Yes, we can recreate your existing design pixel-perfectly in Webflow, or use the migration as an opportunity for a refresh. Most clients opt for a design enhancement since Webflow enables more sophisticated animations and interactions.',
      },
      {
        question: 'What happens to my WordPress plugins?',
        answer: 'We audit every plugin and find Webflow-native or third-party alternatives. Contact forms, SEO tools, analytics, and most functionality have direct replacements. Complex plugins may require custom solutions, which we scope during discovery.',
      },
      {
        question: 'How do you handle WordPress blog content?',
        answer: 'We migrate all blog posts, categories, tags, and media to Webflow CMS. Authors, publish dates, and SEO metadata are preserved. The CMS structure in Webflow is often cleaner and more flexible than WordPress.',
      },
      {
        question: 'Is Webflow really easier than WordPress?',
        answer: 'For marketing teams, dramatically so. No plugin conflicts, no PHP errors, no white screens of death. Your team can make real design changes visually, not just edit text in a page builder.',
      },
    ],
    testimonials: [
      {
        quote: 'After 10 years on WordPress, we were exhausted by constant updates and security patches. Webflow just works. Our team publishes content 4x faster now.',
        author: 'Content Director',
        company: 'Digital Media Company',
        metric: '4x faster publishing',
      },
      {
        quote: 'We had 47 plugins on our WordPress site. Azai migrated us to Webflow with zero functionality loss and our page speed score went from 34 to 96.',
        author: 'Head of Growth',
        company: 'FinTech Startup',
        metric: '96 PageSpeed score',
      },
    ],
  },

  // ============================================
  // DRUPAL
  // ============================================
  'drupal': {
    cmsKey: 'drupal',
    suggestedCompanySize: 'enterprise',
    headline: 'Drupal to Webflow Migration',
    subtitle: 'Calculate your savings switching from Drupal to Webflow.',
    metaTitle: 'Drupal to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Escape complex module dependencies',
      'No more security patch emergencies',
      'Empower marketing without developers',
      'Cut 80% of ongoing maintenance costs',
    ],
    faqs: [
      {
        question: 'Can Webflow handle complex content structures like Drupal?',
        answer: 'Yes. Webflow CMS supports multi-reference fields, nested collections, and rich content structures. While different from Drupal\'s entity system, we can recreate most content architectures. Very complex data relationships may use Airtable or external databases.',
      },
      {
        question: 'What about Drupal Views and custom modules?',
        answer: 'Webflow\'s CMS filtering and dynamic lists replace most Views functionality. Custom modules are evaluated individually—many have SaaS replacements, others can be rebuilt as serverless functions or third-party integrations.',
      },
      {
        question: 'How do you handle Drupal multisite setups?',
        answer: 'Webflow doesn\'t have native multisite, but we design solutions using Webflow\'s site plans, shared component libraries, and synchronized content strategies. For true multisite needs, we can architect hub-and-spoke models.',
      },
      {
        question: 'Our Drupal site has thousands of pages. Can Webflow handle it?',
        answer: 'Webflow handles large sites well—CMS plans support up to 10,000 items per collection. For very large sites, we implement strategic content architecture and may use external databases for unlimited scale.',
      },
    ],
    testimonials: [
      {
        quote: 'Drupal migrations are hard, but Azai made it painless. They preserved our complex taxonomy structure and our editors actually enjoy using the new CMS.',
        author: 'CTO',
        company: 'Healthcare Organization',
        metric: '70% less training time',
      },
      {
        quote: 'We were spending $15K/month on Drupal developers just to keep the lights on. Now our marketing team handles everything themselves.',
        author: 'VP Marketing',
        company: 'Enterprise B2B',
        metric: '$180K annual savings',
      },
    ],
  },

  // ============================================
  // HUBSPOT CMS
  // ============================================
  'hubspot-cms': {
    cmsKey: 'hubspot',
    suggestedCompanySize: 'midmarket',
    headline: 'HubSpot CMS Alternative',
    subtitle: 'Keep HubSpot for marketing. Upgrade your website to Webflow.',
    metaTitle: 'HubSpot CMS to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Break free from HubL template limitations',
      'True design freedom for your brand',
      'Seamless HubSpot forms & tracking integration',
      'Lower total cost of ownership',
    ],
    faqs: [
      {
        question: 'Can we still use HubSpot for marketing if we switch to Webflow?',
        answer: 'Absolutely! Most clients keep HubSpot for CRM, email marketing, and automation while using Webflow for their website. HubSpot forms embed perfectly in Webflow, and tracking scripts work identically.',
      },
      {
        question: 'Why is Webflow better than HubSpot CMS for websites?',
        answer: 'HubSpot CMS is a marketing tool that happens to build websites. Webflow is a professional web platform. You get true design freedom, better performance, more sophisticated interactions, and a visual builder that doesn\'t feel like a template.',
      },
      {
        question: 'What about HubSpot CMS personalization features?',
        answer: 'While HubSpot\'s native smart content won\'t work in Webflow, you can achieve similar personalization using HubSpot\'s API, third-party tools like Mutiny or Intellimize, or custom JavaScript solutions.',
      },
      {
        question: 'How do you migrate HubSpot blog content?',
        answer: 'We export all HubSpot blog posts, preserve SEO settings, and migrate to Webflow CMS. HubSpot-specific features like topic clusters are recreated using Webflow\'s collection relationships.',
      },
    ],
    testimonials: [
      {
        quote: 'We love HubSpot for marketing automation but hated the website limitations. Moving to Webflow gave us the design flexibility we needed while keeping all our HubSpot workflows intact.',
        author: 'Director of Demand Gen',
        company: 'Marketing Technology Company',
        metric: '2x conversion rate',
      },
      {
        quote: 'The HubSpot CMS drag-and-drop editor is a joke compared to Webflow. Our team can now build landing pages in hours instead of days.',
        author: 'Growth Marketing Lead',
        company: 'B2B SaaS',
        metric: '75% faster page builds',
      },
    ],
  },

  // ============================================
  // CONTENTFUL
  // ============================================
  'contentful': {
    cmsKey: 'contentful',
    suggestedCompanySize: 'enterprise',
    headline: 'Contentful Alternative',
    subtitle: 'Simplify your stack. Migrate from Contentful to Webflow.',
    metaTitle: 'Contentful to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Visual editing instead of JSON fields',
      'No frontend framework dependency',
      'Hosting included—no separate infrastructure',
      'Dramatically lower complexity and cost',
    ],
    faqs: [
      {
        question: 'Isn\'t Contentful more flexible than Webflow?',
        answer: 'Contentful is more flexible for developers building custom frontends. But that flexibility comes at a cost—you need React/Next.js developers, separate hosting, and complex deployment pipelines. For most marketing sites, Webflow provides enough flexibility with far less complexity.',
      },
      {
        question: 'How do you migrate Contentful content models?',
        answer: 'We map your Contentful content types to Webflow CMS collections. Rich text, references, and media are all supported. Very complex nested structures may require slight architectural adjustments.',
      },
      {
        question: 'What about Contentful\'s localization features?',
        answer: 'Webflow supports localization natively with Webflow Localization. We migrate content across all locales and set up the same language structure in Webflow.',
      },
      {
        question: 'We have a custom frontend. Can we still use Webflow?',
        answer: 'Webflow can be your entire stack (CMS + hosting + frontend), or you can use Webflow as a visual builder and export code. For headless needs, consider Webflow + their API or a hybrid approach.',
      },
    ],
    testimonials: [
      {
        quote: 'Contentful promised flexibility but delivered complexity. We needed a developer for every content change. Webflow gave our marketing team true independence.',
        author: 'CMO',
        company: 'DTC Brand',
        metric: '90% fewer developer tickets',
      },
      {
        quote: 'Our Contentful + Next.js stack cost $8K/month in hosting and developer maintenance. Webflow is $200/month and our site is faster.',
        author: 'Head of Engineering',
        company: 'Series B Startup',
        metric: '$93K annual savings',
      },
    ],
  },

  // ============================================
  // SITECORE
  // ============================================
  'sitecore': {
    cmsKey: 'sitecore',
    suggestedCompanySize: 'enterprise',
    headline: 'Sitecore Alternative',
    subtitle: 'Escape enterprise complexity. Calculate your Webflow savings.',
    metaTitle: 'Sitecore to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Eliminate $200K+ annual platform costs',
      'Months of implementation → weeks',
      'No more Sitecore developer shortage',
      'Self-service for marketing teams',
    ],
    faqs: [
      {
        question: 'Can Webflow really replace Sitecore for enterprise?',
        answer: 'For marketing websites, absolutely. Sitecore\'s power is in personalization and complex digital experiences. If you\'re using 20% of Sitecore\'s features (like most companies), Webflow delivers what you need at 10% of the cost.',
      },
      {
        question: 'What about Sitecore personalization and analytics?',
        answer: 'Webflow integrates with best-of-breed personalization tools (Mutiny, Intellimize, Dynamic Yield) and analytics platforms (Amplitude, Mixpanel, GA4). You often get better capabilities than Sitecore\'s built-in features.',
      },
      {
        question: 'How do you handle Sitecore\'s component architecture?',
        answer: 'We recreate Sitecore components as Webflow symbols and component libraries. The visual building experience is actually superior—marketers can modify components without developer involvement.',
      },
      {
        question: 'Our Sitecore implementation took 18 months. How long for Webflow?',
        answer: 'Typically 10-16 weeks for a full enterprise migration. Webflow\'s visual approach eliminates most of the custom development that makes Sitecore implementations so lengthy.',
      },
    ],
    testimonials: [
      {
        quote: 'We spent $1.2M implementing Sitecore and $300K/year maintaining it. Webflow does everything we actually needed for a fraction of the cost. Should have switched years ago.',
        author: 'Digital Director',
        company: 'Fortune 500 Company',
        metric: '$1.5M saved over 3 years',
      },
      {
        quote: 'Finding Sitecore developers was impossible. Our Webflow site launches updates same-day instead of waiting weeks for the dev queue.',
        author: 'VP Digital Experience',
        company: 'Global Enterprise',
        metric: '10x faster updates',
      },
    ],
  },

  // ============================================
  // ADOBE AEM
  // ============================================
  'aem': {
    cmsKey: 'aem',
    suggestedCompanySize: 'enterprise',
    headline: 'Adobe AEM Alternative',
    subtitle: 'Calculate your savings migrating from AEM to Webflow.',
    metaTitle: 'Adobe AEM to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Escape $500K+ annual AEM costs',
      'No more 6-month implementation cycles',
      'Marketing autonomy without IT tickets',
      'Modern stack without legacy baggage',
    ],
    faqs: [
      {
        question: 'Can Webflow handle enterprise-scale like AEM?',
        answer: 'For marketing websites and digital experiences, yes. Webflow Enterprise serves Fortune 500 companies. AEM\'s complexity is only justified for very specific use cases like DAM-heavy multi-brand architectures.',
      },
      {
        question: 'What about Adobe Experience Cloud integration?',
        answer: 'Webflow integrates with Adobe Analytics, Adobe Target, and other Experience Cloud products via JavaScript and APIs. You keep your Adobe marketing stack while gaining a better website platform.',
      },
      {
        question: 'How do you migrate AEM components and templates?',
        answer: 'We audit your AEM component library, recreate them as Webflow symbols, and build a comprehensive design system. The result is often cleaner and more maintainable than the original AEM implementation.',
      },
      {
        question: 'Our organization requires SSO and enterprise security.',
        answer: 'Webflow Enterprise includes SSO (SAML 2.0), role-based permissions, audit logs, and SOC 2 compliance. Most enterprise security requirements are met out of the box.',
      },
    ],
    testimonials: [
      {
        quote: 'AEM was the most expensive mistake in our digital history. The migration to Webflow paid for itself in 4 months through licensing savings alone.',
        author: 'CIO',
        company: 'Multi-National Corporation',
        metric: 'ROI in 4 months',
      },
      {
        quote: 'Our AEM team was 12 people. Our Webflow team is 3, and we ship more work. The math speaks for itself.',
        author: 'Director of Web Operations',
        company: 'Enterprise Retail',
        metric: '75% team reduction',
      },
    ],
  },

  // ============================================
  // SQUARESPACE
  // ============================================
  'squarespace': {
    cmsKey: 'squarespace',
    suggestedCompanySize: 'startup',
    headline: 'Squarespace to Webflow Upgrade',
    subtitle: 'Graduate from templates. Calculate your Webflow potential.',
    metaTitle: 'Squarespace to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Break free from template limitations',
      'Professional animations and interactions',
      'True custom design for your brand',
      'CMS that scales with your business',
    ],
    faqs: [
      {
        question: 'Why switch from Squarespace to Webflow?',
        answer: 'Squarespace is great for getting started, but growing businesses hit its limits fast—template constraints, limited CMS, basic animations. Webflow gives you agency-level design capabilities without the agency dependency.',
      },
      {
        question: 'Is Webflow harder to use than Squarespace?',
        answer: 'The learning curve is steeper initially, but the payoff is enormous. Think of it as graduating from PowerPoint to professional design tools. Most teams become proficient within 2-3 weeks.',
      },
      {
        question: 'Can you migrate my Squarespace content?',
        answer: 'Yes, we migrate all pages, blog posts, products, and media. Squarespace exports are straightforward, and we recreate your content structure in Webflow\'s more powerful CMS.',
      },
      {
        question: 'What about Squarespace scheduling and email?',
        answer: 'Squarespace\'s add-on features (scheduling, email, etc.) have better standalone alternatives. We help you choose and integrate the right tools—Calendly, Mailchimp, or similar—that outperform Squarespace\'s built-ins.',
      },
    ],
    testimonials: [
      {
        quote: 'We were embarrassed to send investors to our Squarespace site. Webflow gave us a site that looks like we hired a top agency—because we did.',
        author: 'Founder',
        company: 'Venture-Backed Startup',
        metric: 'Professional brand presence',
      },
      {
        quote: 'Every competitor in our space uses the same Squarespace templates. Our Webflow site immediately set us apart and increased demo requests by 40%.',
        author: 'Head of Marketing',
        company: 'B2B Software',
        metric: '40% more demos',
      },
    ],
  },

  // ============================================
  // KENTICO
  // ============================================
  'kentico': {
    cmsKey: 'kentico',
    suggestedCompanySize: 'enterprise',
    headline: 'Kentico Alternative',
    subtitle: 'Modernize your web platform. Calculate Webflow savings.',
    metaTitle: 'Kentico to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Escape .NET developer dependency',
      'Modern visual editing experience',
      'Faster time-to-market for campaigns',
      'Significant licensing cost reduction',
    ],
    faqs: [
      {
        question: 'Can Webflow replace Kentico\'s digital marketing features?',
        answer: 'Kentico bundles many features you may not use. Webflow focuses on being the best website platform, integrating with best-of-breed tools for email (Klaviyo), marketing automation (HubSpot), and personalization (Mutiny).',
      },
      {
        question: 'What about Kentico Xperience\'s e-commerce?',
        answer: 'For e-commerce, we typically recommend Webflow + Shopify (headless) or Webflow + Snipcart. These purpose-built solutions outperform Kentico\'s e-commerce module.',
      },
      {
        question: 'How do you handle Kentico custom widgets?',
        answer: 'We audit all custom widgets and page builder components, then recreate them as Webflow symbols with equivalent or improved functionality. Most Kentico customizations have cleaner Webflow implementations.',
      },
      {
        question: 'Our team knows .NET. Is Webflow too different?',
        answer: 'Webflow is actually easier—it\'s visual, not code-based. Your .NET team can learn Webflow quickly, and many find they prefer it. For custom logic, Webflow supports JavaScript and serverless functions.',
      },
    ],
    testimonials: [
      {
        quote: 'Kentico served us well for years, but finding developers became impossible and costs kept rising. Webflow modernized our entire web operation.',
        author: 'IT Director',
        company: 'Manufacturing Company',
        metric: 'No more developer shortage',
      },
      {
        quote: 'We were locked into Kentico\'s upgrade cycle and facing a major version migration. Switching to Webflow was faster and cheaper than upgrading.',
        author: 'Digital Manager',
        company: 'Professional Services Firm',
        metric: '50% lower TCO',
      },
    ],
  },

  // ============================================
  // JOOMLA
  // ============================================
  'joomla': {
    cmsKey: 'joomla',
    suggestedCompanySize: 'startup',
    headline: 'Joomla to Webflow Migration',
    subtitle: 'Modernize your website. Calculate your Webflow savings.',
    metaTitle: 'Joomla to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Escape outdated extension ecosystem',
      'Modern design without PHP limitations',
      'Security without constant patching',
      'Intuitive editing for your whole team',
    ],
    faqs: [
      {
        question: 'Is Joomla really outdated?',
        answer: 'Joomla is still maintained, but its market share and ecosystem have declined significantly. Finding quality extensions, themes, and developers is increasingly difficult. Webflow represents the modern approach to web development.',
      },
      {
        question: 'Can you migrate our Joomla articles and K2 content?',
        answer: 'Yes, we migrate all Joomla content including articles, categories, custom fields, and K2 items. Everything transfers to Webflow CMS with proper structure and SEO preservation.',
      },
      {
        question: 'What about Joomla extensions we rely on?',
        answer: 'We audit every extension and find modern replacements. Most Joomla extensions solve problems that Webflow handles natively or through superior third-party integrations.',
      },
      {
        question: 'Our Joomla site has user registration and membership.',
        answer: 'Webflow integrates with membership platforms like Memberstack, Memberful, or Outseta that provide better user experiences than Joomla\'s community extensions.',
      },
    ],
    testimonials: [
      {
        quote: 'Our Joomla site was from 2012. Finding someone who could work on it was nearly impossible. Webflow brought us into the modern era.',
        author: 'Operations Manager',
        company: 'Non-Profit Organization',
        metric: 'Modern, maintainable site',
      },
      {
        quote: 'Joomla security updates were a full-time job. Since moving to Webflow, I haven\'t thought about security once—it\'s just handled.',
        author: 'IT Manager',
        company: 'Small Business',
        metric: 'Zero security incidents',
      },
    ],
  },

  // ============================================
  // SANITY
  // ============================================
  'sanity': {
    cmsKey: 'sanity',
    suggestedCompanySize: 'midmarket',
    headline: 'Sanity.io Alternative',
    subtitle: 'Simplify your headless stack. Migrate to Webflow.',
    metaTitle: 'Sanity to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'Visual editing instead of GROQ queries',
      'No frontend framework maintenance',
      'Hosting included—one less vendor',
      'Marketing team independence',
    ],
    faqs: [
      {
        question: 'Isn\'t Sanity more powerful than Webflow?',
        answer: 'Sanity is incredibly flexible for developers, but that flexibility requires developer involvement for most changes. Webflow trades some flexibility for marketing team autonomy—a worthwhile trade for most organizations.',
      },
      {
        question: 'Can Webflow handle Sanity\'s structured content?',
        answer: 'Webflow CMS handles most structured content needs—references, rich text, images, and custom fields. Very complex content graphs may need architectural adjustments, but 90% of Sanity implementations translate cleanly.',
      },
      {
        question: 'What about Sanity Studio customization?',
        answer: 'Webflow\'s Editor provides a cleaner, more intuitive editing experience for content teams. While less customizable than Sanity Studio, most clients find it more user-friendly.',
      },
      {
        question: 'We use Sanity with Next.js. What happens to our frontend?',
        answer: 'Webflow replaces both your CMS and frontend. No more React maintenance, no more Vercel/Netlify builds. If you need custom functionality, Webflow supports embedded JavaScript and external integrations.',
      },
    ],
    testimonials: [
      {
        quote: 'Sanity + Next.js was the "right" technical choice, but marketing couldn\'t update the site without engineering. Webflow fixed our velocity problem.',
        author: 'VP Engineering',
        company: 'Tech Startup',
        metric: '10x content velocity',
      },
      {
        quote: 'We spent 6 months building a custom Sanity frontend. Webflow would have taken 6 weeks. Live and learn.',
        author: 'CTO',
        company: 'Digital Agency',
        metric: '6 months saved',
      },
    ],
  },

  // ============================================
  // STRAPI
  // ============================================
  'strapi': {
    cmsKey: 'strapi',
    suggestedCompanySize: 'midmarket',
    headline: 'Strapi Alternative',
    subtitle: 'Ditch the headless complexity. Calculate Webflow savings.',
    metaTitle: 'Strapi to Webflow Migration Calculator | Azai Studios',
    valueProps: [
      'No server maintenance or hosting setup',
      'Visual builder instead of API-first',
      'Marketing autonomy without developers',
      'All-in-one platform vs pieced-together stack',
    ],
    faqs: [
      {
        question: 'Why move from Strapi to Webflow?',
        answer: 'Strapi requires you to manage hosting, build a frontend, and maintain both over time. Webflow is a complete platform—you focus on content and design, not DevOps. The trade-off is worth it for most marketing teams.',
      },
      {
        question: 'Strapi is open-source and free. Isn\'t it cheaper?',
        answer: 'Strapi itself is free, but hosting ($100-500/mo), frontend development ($50-200/hr), and ongoing maintenance add up fast. Total cost of ownership for Webflow is often lower when you factor in all the hidden costs of headless.',
      },
      {
        question: 'How do you migrate Strapi content types?',
        answer: 'We map Strapi content types to Webflow CMS collections. Fields, relationships, and media all transfer. Dynamic zones require some restructuring, but we maintain content integrity throughout.',
      },
      {
        question: 'What if we need API access?',
        answer: 'Webflow has a CMS API if you need programmatic access. For most use cases, Webflow\'s native CMS is sufficient. If you need true headless flexibility, we can discuss hybrid architectures.',
      },
    ],
    testimonials: [
      {
        quote: 'Self-hosting Strapi sounded great until our server went down on a Saturday. Webflow\'s managed infrastructure gives us peace of mind.',
        author: 'Technical Lead',
        company: 'Growing Startup',
        metric: '99.99% uptime',
      },
      {
        quote: 'Our Strapi + Nuxt setup was impressive technically, but it took developers to change a headline. Webflow democratized our website.',
        author: 'Marketing Director',
        company: 'E-commerce Company',
        metric: 'Marketing independence',
      },
    ],
  },
};

// Helper to get a variant by slug (case-insensitive)
export const getVariantBySlug = (slug) => {
  if (!slug) return null;
  const normalized = slug.toLowerCase().trim();
  return PLATFORM_VARIANTS[normalized] || null;
};

// Get all available variant slugs (for validation/documentation)
export const getAvailableVariants = () => Object.keys(PLATFORM_VARIANTS);
